import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";

/*
  Shared layout for PAUS legal documents (Terms of Use, Privacy Policy).
  Content is passed in as plain data, so the text can be edited without touching the layout.

  Block types inside a section's `blocks` array:
    "Some text"                         -> paragraph
    { h: "Heading" }                    -> small sub-heading
    { caps: "TEXT" }                    -> highlighted legal notice (disclaimers etc.)
    { crisis: "TEXT" }                  -> crisis notice with tap-to-call helpline buttons
    { list: ["a", ["Label", "text"]] }  -> bullet list (optionally with bold label)
    { table: { head: [...], rows: [[...], ...] } }
    { dialog: { title, body, buttons: [...] } } -> mock of an in-app consent prompt
    { contact: true }                   -> Grievance Officer contact card
*/

const EMAIL = "paus@m3global.co.in";
const ADDRESS =
  "2nd Floor, Old No. 877 / New No. 196, E.V.R. Periyar Salai, Poonamallee High Road, Flowers Road, Purasawalkam, Chennai, Tamil Nadu 600084, India";

// Turns every occurrence of the PAUS email into a mailto link
function withLinks(text) {
  if (typeof text !== "string") return text;
  const parts = text.split(EMAIL);
  return parts.map((part, i) => (
    <React.Fragment key={i}>
      {part}
      {i < parts.length - 1 && (
        <a href={`mailto:${EMAIL}`} className="text-[#2f8f45] underline underline-offset-2 hover:text-[#163248] break-all">
          {EMAIL}
        </a>
      )}
    </React.Fragment>
  ));
}

function Block({ block }) {
  if (typeof block === "string") {
    return <p className="text-slate-700 leading-8">{withLinks(block)}</p>;
  }

  if (block.h) {
    return <h3 className="text-base font-semibold text-[#163248] pt-2">{block.h}</h3>;
  }

  if (block.caps) {
    return (
      <div className="border-l-4 border-[#163248] bg-[#f6f9fc] rounded-r-xl px-5 py-4">
        <p className="text-[13px] leading-7 text-[#163248] font-medium">{block.caps}</p>
      </div>
    );
  }

  if (block.crisis) {
    return (
      <div className="border border-red-200 bg-red-50 rounded-2xl p-5">
        <p className="text-[13px] leading-7 text-red-900 font-medium mb-4">{block.crisis}</p>
        <div className="flex flex-wrap gap-2">
          <a href="tel:112" className="bg-red-700 text-white text-sm font-semibold px-4 py-2 rounded-lg hover:bg-red-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-red-400">
            Call 112 · Emergency
          </a>
          <a href="tel:14416" className="bg-white border border-red-300 text-red-800 text-sm font-semibold px-4 py-2 rounded-lg hover:bg-red-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-red-400">
            Call 14416 · Tele-MANAS
          </a>
          <a href="tel:18008914416" className="bg-white border border-red-300 text-red-800 text-sm font-semibold px-4 py-2 rounded-lg hover:bg-red-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-red-400">
            Call 1-800-891-4416
          </a>
        </div>
      </div>
    );
  }

  if (block.list) {
    return (
      <ul className="space-y-3">
        {block.list.map((item, i) => {
          const [label, text] = Array.isArray(item) ? item : [null, item];
          return (
            <li key={i} className="flex gap-3 text-slate-700 leading-7">
              <span className="mt-[11px] w-1.5 h-1.5 rounded-full bg-[#6ec87a] flex-shrink-0" />
              <span>
                {label && <strong className="font-semibold text-[#163248]">{label}: </strong>}
                {withLinks(text)}
              </span>
            </li>
          );
        })}
      </ul>
    );
  }

  if (block.table) {
    const { head, rows } = block.table;
    return (
      <>
        {/* Desktop table */}
        <div className="hidden md:block overflow-x-auto rounded-2xl border border-slate-200">
          <table className="w-full text-sm text-left">
            <thead className="bg-[#163248] text-white">
              <tr>
                {head.map((h) => (
                  <th key={h} className="px-4 py-3 font-semibold align-top">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row, r) => (
                <tr key={r} className="border-t border-slate-200 even:bg-[#f6f9fc]">
                  {row.map((cell, c) => (
                    <td key={c} className={`px-4 py-3 align-top leading-6 ${c === 0 ? "font-semibold text-[#163248] w-48" : "text-slate-700"}`}>
                      {cell}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Mobile: one card per row */}
        <div className="md:hidden space-y-3">
          {rows.map((row, r) => (
            <div key={r} className="border border-slate-200 rounded-2xl p-4">
              <p className="font-semibold text-[#163248] mb-2">{row[0]}</p>
              {row.slice(1).map((cell, c) => (
                <div key={c} className="mb-2 last:mb-0">
                  <p className="text-xs text-slate-500">{head[c + 1]}</p>
                  <p className="text-sm text-slate-700 leading-6">{cell}</p>
                </div>
              ))}
            </div>
          ))}
        </div>
      </>
    );
  }

  if (block.dialog) {
    const { title, body, buttons } = block.dialog;
    return (
      <figure className="max-w-md mx-auto border border-slate-300 rounded-2xl shadow-lg overflow-hidden">
        <figcaption className="bg-[#163248] text-white text-xs font-semibold px-5 py-3">{title}</figcaption>
        <div className="p-5 bg-white">
          <p className="text-sm text-slate-700 leading-6 mb-4">{body}</p>
          <div className="flex justify-end gap-2">
            {buttons.map((b, i) => (
              <span
                key={b}
                className={`text-sm px-4 py-2 rounded-lg ${i === buttons.length - 1 ? "bg-[#6ec87a] text-[#0a1628] font-semibold" : "border border-slate-300 text-slate-600"}`}
              >
                {b}
              </span>
            ))}
          </div>
        </div>
      </figure>
    );
  }

  if (block.contact) {
    return (
      <address className="not-italic bg-[#163248] text-slate-200 rounded-2xl p-6 text-sm leading-7">
        <p className="text-white font-semibold text-base">Mr. R. K. Anand</p>
        <p className="text-[#6ec87a] mb-3">Grievance Redressal Officer &amp; Data Protection Officer</p>
        <p>M3 Global Medicare LLP</p>
        <p className="mb-3">{ADDRESS}</p>
        <a href={`mailto:${EMAIL}`} className="inline-block bg-[#6ec87a] text-[#0a1628] font-semibold px-4 py-2 rounded-lg hover:opacity-90">
          Email {EMAIL}
        </a>
      </address>
    );
  }

  return null;
}

export default function LegalPage({ docTitle, effectiveDate, summary, sections, otherDoc }) {
  const [active, setActive] = useState(sections[0]?.id);
  const currentYear = new Date().getFullYear();

  // Start at top, unless the URL points at a specific section (e.g. /paus/privacy-policy#s-11)
  useEffect(() => {
    const hash = window.location.hash.slice(1);
    const el = hash && document.getElementById(hash);
    if (el) el.scrollIntoView();
    else window.scrollTo(0, 0);
    document.title = `${docTitle} | PAUS by M3 Global Medicare LLP`;
  }, [docTitle]);

  // Highlight the section currently on screen in the sidebar
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries.filter((e) => e.isIntersecting);
        if (visible.length) setActive(visible[0].target.id);
      },
      { rootMargin: "-140px 0px -60% 0px" }
    );
    sections.forEach((s) => {
      const el = document.getElementById(s.id);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, [sections]);

  const toc = (
    <ol className="space-y-1 text-sm">
      {sections.map((s) => (
        <li key={s.id}>
          <a
            href={`#${s.id}`}
            className={`flex gap-2 px-3 py-1.5 rounded-lg transition-colors ${
              active === s.id ? "bg-[#6ec87a]/15 text-[#163248] font-semibold" : "text-slate-600 hover:text-[#163248] hover:bg-slate-100"
            }`}
          >
            <span className="tabular-nums text-slate-400 w-5 flex-shrink-0">{s.num}</span>
            <span>{s.title}</span>
          </a>
        </li>
      ))}
    </ol>
  );

  return (
    <div className="min-h-screen bg-white text-slate-800 font-sans">
      {/* Header band */}
      <section className="bg-[#163248] text-white">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-12 md:py-16">
          <Link to="/paus" className="inline-flex items-center gap-2 text-sm text-slate-300 hover:text-[#6ec87a] mb-8">
            <img src="/PAUS Logo_White.png" alt="PAUS" className="h-8 w-auto object-contain" />
            <span>Back to PAUS</span>
          </Link>
          <h1 className="text-3xl md:text-5xl font-semibold mb-4">{docTitle}</h1>
          <p className="text-slate-300 text-sm md:text-base">
            M3 Global Medicare LLP · Effective {effectiveDate}
          </p>

          {/* Switch between the two documents */}
          <div className="mt-8 inline-flex bg-white/10 rounded-xl p-1 text-sm">
            <span className="px-4 py-2 rounded-lg bg-white text-[#163248] font-semibold">{docTitle}</span>
            <Link to={otherDoc.to} className="px-4 py-2 rounded-lg text-slate-200 hover:text-white">
              {otherDoc.label}
            </Link>
          </div>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-6 md:px-10 py-10 md:py-14 grid lg:grid-cols-[260px_1fr] gap-10 lg:gap-14">
        {/* Sidebar contents (desktop) */}
        <aside className="hidden lg:block">
          <nav aria-label="Sections" className="sticky top-36 max-h-[calc(100vh-10rem)] overflow-y-auto pr-2">
            <p className="text-xs font-semibold text-slate-500 px-3 mb-3">Contents</p>
            {toc}
          </nav>
        </aside>

        <main className="min-w-0 max-w-3xl">
          {/* Contents (mobile) */}
          <details className="lg:hidden border border-slate-200 rounded-2xl mb-8 group">
            <summary className="cursor-pointer list-none px-5 py-4 font-semibold text-[#163248] flex justify-between items-center">
              Contents
              <svg className="w-4 h-4 transition-transform group-open:rotate-180" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </summary>
            <div className="px-2 pb-4">{toc}</div>
          </details>

          {summary && (
            <div className="bg-[#6ec87a]/10 border border-[#6ec87a]/40 rounded-2xl p-6 mb-10">
              <p className="font-semibold text-[#163248] mb-2">{summary.title}</p>
              <p className="text-slate-700 text-sm leading-7">{summary.text}</p>
            </div>
          )}

          <div className="space-y-12">
            {sections.map((s) => (
              <section key={s.id} id={s.id} className="scroll-mt-36">
                <h2 className="flex items-baseline gap-3 text-xl md:text-2xl font-semibold text-[#163248] mb-5 pb-3 border-b border-slate-200">
                  <span className="text-[#6ec87a] tabular-nums">{s.num}.</span>
                  {s.title}
                </h2>
                <div className="space-y-5">
                  {s.blocks.map((b, i) => (
                    <Block key={i} block={b} />
                  ))}
                </div>
              </section>
            ))}
          </div>

          <p className="mt-14 text-sm text-slate-500">
            Questions about this document? Write to{" "}
            <a href={`mailto:${EMAIL}`} className="text-[#2f8f45] underline underline-offset-2">{EMAIL}</a>.
          </p>
        </main>
      </div>

      {/* Footer */}
      <footer className="bg-[#f6f9fc] border-t border-slate-200 text-slate-600">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-6 flex flex-col md:flex-row items-center justify-between gap-3 text-sm">
          <p>© {currentYear} PAUS. A product of <span className="text-[#2f8f45]">M3 Global Medicare LLP</span>.</p>
          <div className="flex flex-wrap items-center gap-4">
            <Link to="/paus/terms-of-use" className="hover:text-[#2f8f45]">Terms of Use</Link>
            <Link to="/paus/privacy-policy" className="hover:text-[#2f8f45]">Privacy Policy</Link>
            <Link to="/" className="hover:text-[#2f8f45]">← Back to M3 Global</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}